import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';

const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ChangePasswordPage = lazy(() => import('./pages/ChangePasswordPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const SalesDashboardPage = lazy(() => import('./pages/SalesDashboardPage'));
const InventoryPage = lazy(() => import('./pages/InventoryPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const ShopsPage = lazy(() => import('./pages/ShopsPage'));
const SyncPage = lazy(() => import('./pages/SyncPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const PurchaseOrdersPage = lazy(() => import('./pages/PurchaseOrdersPage'));
const WMSPage = lazy(() => import('./pages/WMSPage'));
const AutomationPage = lazy(() => import('./pages/AutomationPage'));
const ShippingLabelsPage = lazy(() => import('./pages/ShippingLabelsPage'));
const DropshipPage = lazy(() => import('./pages/DropshipPage'));
const AmazonToRakutenPage = lazy(() => import('./pages/AmazonToRakutenPage'));
const ProductFiltersPage = lazy(() => import('./pages/ProductFiltersPage'));
const ImportCsvPage = lazy(() => import('./pages/ImportCsvPage'));
const PipelinePage = lazy(() => import('./pages/PipelinePage'));
const PricingSettingsPage = lazy(() => import('./pages/PricingSettingsPage'));
const ListingFlagsPage = lazy(() => import('./pages/ListingFlagsPage'));
const RakutenVisibilityPage = lazy(() => import('./pages/RakutenVisibilityPage'));
const BrandMallSummaryPage = lazy(() => import('./pages/BrandMallSummaryPage'));
const OverseasSourcingPage = lazy(() => import('./pages/OverseasSourcingPage'));
const EbayListingPage = lazy(() => import('./pages/EbayListingPage'));
const SeoPage = lazy(() => import('./pages/SeoPage'));
const SiteMastersPage = lazy(() => import('./pages/SiteMastersPage'));
const CostDefectsPage = lazy(() => import('./pages/CostDefectsPage'));
const BannerManagementPage = lazy(() => import('./pages/BannerManagementPage'));
const ImageSelectionRulesPage = lazy(() => import('./pages/ImageSelectionRulesPage'));
const PromptTemplatesPage = lazy(() => import('./pages/PromptTemplatesPage'));
const ReviewQueuePage = lazy(() => import('./pages/ReviewQueuePage'));
const PipelineMetricsPage = lazy(() => import('./pages/PipelineMetricsPage'));
const RakutenSyncMonitorPage = lazy(() => import('./pages/RakutenSyncMonitorPage'));
const ScrapingIngestMonitorPage = lazy(() => import('./pages/ScrapingIngestMonitorPage'));
const SourceComparePage = lazy(() => import('./pages/SourceComparePage'));

// 認証必須ルートのラッパー
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// 管理者専用ルート
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      }
    >
    <Routes>
      {/* 認証不要ルート */}
      <Route path="/login" element={<LoginPage />} />

      {/* 認証必須ルート */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="change-password" element={<ChangePasswordPage />} />
        
        {/* 業務機能 */}
        <Route path="orders" element={<OrdersPage />} />
        <Route path="sales-dashboard" element={<SalesDashboardPage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="shops" element={<ShopsPage />} />
        <Route path="sync" element={<SyncPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        
        {/* 追加機能 */}
        <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
        <Route path="wms" element={<WMSPage />} />
        <Route path="automation" element={<AutomationPage />} />
        <Route path="shipping-labels" element={<ShippingLabelsPage />} />
        <Route path="dropship" element={<DropshipPage />} />
        <Route path="amazon-to-rakuten" element={<AmazonToRakutenPage />} />
        <Route path="product-filters" element={<ProductFiltersPage />} />
        <Route path="import-csv" element={<ImportCsvPage />} />
        <Route path="pipeline" element={<PipelinePage />} />
        <Route path="pricing-settings" element={<PricingSettingsPage />} />
        <Route path="listing-flags" element={<ListingFlagsPage />} />
        <Route path="rakuten-visibility" element={<RakutenVisibilityPage />} />
        <Route path="brand-mall-summary" element={<BrandMallSummaryPage />} />
        <Route path="ebay-listing" element={<EbayListingPage />} />
        <Route path="overseas-sourcing" element={<OverseasSourcingPage />} />
        <Route path="seo" element={<SeoPage />} />
        <Route path="site-masters" element={<SiteMastersPage />} />
        <Route path="banners" element={<BannerManagementPage />} />
        <Route path="image-selection-rules" element={<ImageSelectionRulesPage />} />
        <Route path="prompt-templates" element={<PromptTemplatesPage />} />
        <Route path="review-queue" element={<ReviewQueuePage />} />
        <Route path="pipeline-metrics" element={<PipelineMetricsPage />} />
        <Route path="rakuten-sync-monitor" element={<RakutenSyncMonitorPage />} />
        <Route path="scraping-ingest-monitor" element={<ScrapingIngestMonitorPage />} />
        <Route path="source-compare" element={<SourceComparePage />} />
        
        {/* 管理者専用 */}
        <Route
          path="cost-defects"
          element={
            <AdminRoute>
              <CostDefectsPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/users"
          element={
            <AdminRoute>
              <UsersPage />
            </AdminRoute>
          }
        />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  );
}

export default App;

